import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import { 
  LayoutDashboard, Users, BookOpen, UserCog, Video, BarChart3,
  Gamepad2, FileText, PenTool, Languages, ClipboardList, Plus,
  Copy, ExternalLink, Eye, Trash2, CheckCircle, Clock, AlertCircle,
  Loader2, Link2, Mail, MailCheck, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';

import { toast } from 'sonner';

const AdminAssessments = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sendingReport, setSendingReport] = useState(null);
  const [newLinkData, setNewLinkData] = useState({
    recipient_name: '',
    recipient_email: '',
    recipient_phone: '',
    notes: ''
  });

  const sidebarItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/users', label: 'User Management', icon: Users },
    { path: '/admin/allocate', label: 'Subject Allocation', icon: BookOpen },
    { path: '/admin/tutors', label: 'Tutor Assignment', icon: UserCog },
    { path: '/tutoring', label: 'Live Sessions', icon: Video },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/assessments', label: 'Assessments', icon: ClipboardList },
    { divider: true, label: 'Content Preview' },
    { path: '/admin/preview/games', label: 'Games Zone', icon: Gamepad2 },
    { path: '/admin/preview/exams', label: 'Exams', icon: FileText },
    { path: '/admin/preview/essays', label: 'Essays', icon: PenTool },
    { path: '/admin/preview/esol-videos', label: 'ESOL Videos', icon: Languages },
  ];

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const res = await api.get('/assessment/links');
      setLinks(res.data.links || []);
    } catch (err) {
      console.error('Failed to fetch assessment links:', err);
    }
    setLoading(false);
  };

  const createNewLink = async () => {
    setCreating(true);
    try {
      const res = await api.post('/assessment/create-link', newLinkData);
      setShowCreateModal(false);
      setNewLinkData({ recipient_name: '', recipient_email: '', recipient_phone: '', notes: '' });
      fetchLinks(); // Refresh list
    } catch (err) {
      console.error('Failed to create link:', err);
    }
    setCreating(false);
  };

  const copyLink = (token) => {
    const fullUrl = `${window.location.origin}/assessment/${token}`;
    navigator.clipboard.writeText(fullUrl);
    setCopied(token);
    setTimeout(() => setCopied(null), 2000);
  };

  const deleteLink = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this assessment link?')) return;
    
    try {
      await api.delete(`/assessment/link/${linkId}`);
      toast.success('Assessment link deleted');
      fetchLinks();
    } catch (err) {
      console.error('Failed to delete link:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete link');
    }
  };

  const sendReport = async (submissionId, isResend = false) => {
    setSendingReport(submissionId);
    try {
      const res = await api.post(`/assessment/send-report/${submissionId}`);
      const emailStatus = res.data?.email_sent?.status;
      if (emailStatus === 'success') {
        toast.success(isResend ? 'Report resent to parent' : 'Report sent to parent');
      } else {
        toast.error(`Email failed: ${res.data?.email_sent?.error || 'Unknown error'}`);
      }
      fetchLinks();
    } catch (err) {
      console.error('Failed to send report:', err);
      toast.error(err.response?.data?.detail || 'Failed to send report email');
    }
    setSendingReport(null);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Completed
          </span>
        );
      case 'started':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />
            In Progress
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
            <AlertCircle className="w-3 h-3" />
            Pending
          </span>
        );
    }
  };

  return (
    <ProLayout sidebarItems={sidebarItems} title="Assessments">
      {/* Create Assessment Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold text-slate-800 mb-4">Create Assessment Link</h3>
            <p className="text-sm text-slate-600 mb-4">
              Record who you're sending this assessment link to (optional but recommended for tracking).
            </p>
            
            <div className="space-y-3 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Recipient Name</label>
                <input
                  type="text"
                  placeholder="Parent's name"
                  value={newLinkData.recipient_name}
                  onChange={(e) => setNewLinkData({ ...newLinkData, recipient_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  placeholder="email@example.com"
                  value={newLinkData.recipient_email}
                  onChange={(e) => setNewLinkData({ ...newLinkData, recipient_email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                <input
                  type="tel"
                  placeholder="Phone number"
                  value={newLinkData.recipient_phone}
                  onChange={(e) => setNewLinkData({ ...newLinkData, recipient_phone: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  placeholder="Any notes about this lead..."
                  value={newLinkData.notes}
                  onChange={(e) => setNewLinkData({ ...newLinkData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  rows={2}
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={createNewLink}
                disabled={creating}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Link'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Initial Assessments</h1>
          <p className="text-slate-600">Create and manage assessment links for potential students</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2"
          data-testid="create-assessment-link"
        >
          <Plus className="w-4 h-4" />
          Create Assessment Link
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <p className="text-slate-500 text-sm">Total Links</p>
          <p className="text-2xl font-bold text-slate-800">{links.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <p className="text-slate-500 text-sm">Completed</p>
          <p className="text-2xl font-bold text-emerald-600">
            {links.filter(l => l.status === 'completed').length}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <p className="text-slate-500 text-sm">In Progress</p>
          <p className="text-2xl font-bold text-amber-600">
            {links.filter(l => l.status === 'started').length}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <p className="text-slate-500 text-sm">Pending</p>
          <p className="text-2xl font-bold text-slate-600">
            {links.filter(l => l.status === 'pending').length}
          </p>
        </div>
      </div>

      {/* Assessment Links Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100">
        <div className="p-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800">Assessment Links</h2>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto" />
          </div>
        ) : links.length === 0 ? (
          <div className="p-8 text-center">
            <Link2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No assessment links created yet</p>
            <p className="text-sm text-slate-400">Create a link to send to potential parents</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Sent To</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Parent/Child</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Year</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {links.map((link) => (
                  <tr key={link.id} className="hover:bg-slate-50">
                    <td className="px-4 py-4">
                      {getStatusBadge(link.status)}
                    </td>
                    <td className="px-4 py-4">
                      {link.recipient_name ? (
                        <div>
                          <p className="font-medium text-slate-800">{link.recipient_name}</p>
                          <p className="text-xs text-slate-500">{link.recipient_email || link.recipient_phone || ''}</p>
                          {link.notes && <p className="text-xs text-slate-400 italic mt-1">{link.notes}</p>}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">Not recorded</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {link.submission_info ? (
                        <div>
                          <p className="font-medium text-slate-800">{link.submission_info.child_name}</p>
                          <p className="text-sm text-slate-500">{link.submission_info.parent_name}</p>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">Not started</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {link.submission_info ? (
                        <span className="font-medium">Year {link.submission_info.child_year_group}</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600">
                      {new Date(link.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4">
                      {link.status === 'completed' ? (
                        link.submission_info?.report_sent_to_parent ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium" data-testid={`email-sent-${link.id}`}>
                            <MailCheck className="w-3 h-3" />
                            Sent
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium" data-testid={`email-not-sent-${link.id}`}>
                            <Mail className="w-3 h-3" />
                            Not Sent
                          </span>
                        )
                      ) : (
                        <span className="text-slate-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        {link.status === 'completed' ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/admin/assessments/${link.submission_id}`)}
                              className="flex items-center gap-1"
                              data-testid={`view-report-${link.id}`}
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => sendReport(link.submission_id, link.submission_info?.report_sent_to_parent)}
                              disabled={sendingReport === link.submission_id}
                              className={`flex items-center gap-1 ${link.submission_info?.report_sent_to_parent ? 'text-emerald-600 border-emerald-200 hover:bg-emerald-50' : 'text-blue-600 border-blue-200 hover:bg-blue-50'}`}
                              data-testid={`send-report-${link.id}`}
                            >
                              {sendingReport === link.submission_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : link.submission_info?.report_sent_to_parent ? (
                                <RefreshCw className="w-4 h-4" />
                              ) : (
                                <Mail className="w-4 h-4" />
                              )}
                              {link.submission_info?.report_sent_to_parent ? 'Resend' : 'Send Report'}
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyLink(link.token)}
                            className="flex items-center gap-1"
                            data-testid={`copy-link-${link.id}`}
                          >
                            {copied === link.token ? (
                              <CheckCircle className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                            {copied === link.token ? 'Copied!' : 'Copy Link'}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteLink(link.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-link-${link.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ProLayout>
  );
};

export default AdminAssessments;
